(ns kyber.router
  (:require
   [cemerick.url :refer [map->query]]
   [applied-science.js-interop :as j]
   [clojure.string :as str]
   [clojure.set :refer [intersection]]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [kyber.components.composite.loading :as loading]
   [kyber.utils :as ku]
   [kyber.i18n.core :as i18n]
   [kyber.feature-flags :refer [feature-enabled?]]))

;; TODO: One difference between this middleware and the v2 middleware
;;       is that v2 supports parallel request. The learner app has to be
;;       modified to support parallel requests before adopting the v2
;;       solution.
(defn route-middleware
  ([container middleware]
   (route-middleware container middleware {}))
  ([container middleware route-options]
   (let [route-params (re-frame/subscribe [:router/route-params])
         route-query (re-frame/subscribe [:router/route-query])
         route-key (re-frame/subscribe [:router/route])
         route (re-frame/subscribe [:router/core])
         bootstrapped-resources (atom {})
         route-state (atom nil)]
     (fn []
       (let [{:keys [is-loading container] :as ctx}
             (reduce
              (fn [{:keys [is-loading] :as middleware-ctx}
                   middleware-fn]
                (if is-loading
                  middleware-ctx
                  (middleware-fn middleware-ctx)))
              {:is-loading false
               :bootstrapped-resources @bootstrapped-resources
               :route-params @route-params
               :route-query @route-query
               :route-state @route-state
               :container container}
              middleware)]
         
         (reset! bootstrapped-resources (:bootstrapped-resources ctx))
         (reset! route-state (:route-state ctx))

         (when (true? (:initialized @route))
           (if (true? is-loading)
             [loading/render (assoc route-options
                                    :fixed? true)]
             [container @route-params @route-query @route-key])))))))

(defn ensure-resource
  [event-key dispatch-fn {:keys [route-params route-query bootstrapped-resources]
                          :as ctx}]
  (let [request (re-frame/subscribe [:request/track-request event-key])
        status (-> request deref :status)
        has-not-bootstrapped (-> bootstrapped-resources event-key nil?)
        ctx* (cond-> ctx
               (true? has-not-bootstrapped)
               (assoc-in [:bootstrapped-resources event-key] true))]

    (when has-not-bootstrapped
      (dispatch-fn (assoc route-params :route-query route-query)))

    (if (true? (contains? #{:loading :never-requested} status))
      (assoc ctx* :is-loading true)
      ctx*)))

(defn ensure-resources
  [events {:keys [route-params route-query] :as ctx}]
  (let [events-keys
        (map :event-key events)

        request-subs
        (map #(re-frame/subscribe [:request/track-request %]) events-keys)

        requests
        (map #(-> %1 deref (assoc :event-key %2)) request-subs events-keys)

        completed-requests
        (filter #(= :success (:status %)) requests)

        never-requested-events
        (->> requests
             (filter #(= :never-requested (:status %)))
             (map #(:event-key %))
             set)

        statuses
        (set (map #(-> % deref :status) request-subs))

        has-not-dispatched
        (filter #(contains? never-requested-events (:event-key %)) events)

        ctx*
        (if (seq completed-requests)
          (reduce (fn [acc {:keys [event-key]}]
                    (assoc-in acc [:bootstrapped-resources event-key] true))
                  ctx
                  completed-requests)
          ctx)]

    (when (seq has-not-dispatched)
      (doseq [{:keys [dispatch-fn]} has-not-dispatched]
        (dispatch-fn (assoc route-params :route-query route-query))))

    (if (seq (intersection statuses #{:loading :never-requested}))
      (assoc ctx* :is-loading true)
      ctx*)))

(defn ensure-resources-non-blocking
  [events {:keys [route-params route-query] :as ctx}]
  (let [events-keys
        (map :event-key events)

        request-subs
        (map #(re-frame/subscribe [:request/track-request %]) events-keys)

        requests
        (map #(-> %1 deref (assoc :event-key %2)) request-subs events-keys)

        dispatched-requests
        (filter #(contains? #{:loading :success} (:status %)) requests)

        never-requested-events
        (->> requests
             (filter #(= :never-requested (:status %)))
             (map #(:event-key %))
             set)

        statuses
        (set (map #(-> % deref :status) request-subs))

        has-not-dispatched
        (filter #(contains? never-requested-events (:event-key %)) events)

        ctx*
        (if (seq dispatched-requests)
          (reduce (fn [acc {:keys [event-key]}]
                    (assoc-in acc [:bootstrapped-resources event-key] true))
                  ctx
                  dispatched-requests)
          ctx)]

    (when (seq has-not-dispatched)
      (doseq [{:keys [dispatch-fn]} has-not-dispatched]
        (dispatch-fn (assoc route-params :route-query route-query))))

    (if (contains? statuses :never-requested)
      (assoc ctx* :is-loading true)
      ctx*)))

(defn protect
  "Prevents unauthorized access"
  [{:keys [redirect-with]}]
  (fn [ctx]
    (let [is-authenticated
          @(re-frame/subscribe [:authentication/is-authenticated?])

          user-attributes
          @(re-frame/subscribe [:authentication/user-attributes])

          is-bootstrapped
          @(re-frame/subscribe [:bootstrap/finished?])]

      (when (feature-enabled? :console-log)
        (js/console.log "[protect middleware] is-bootstrapped: " is-bootstrapped)
        (js/console.log "[protect middleware] @is-authenticated: " is-authenticated)
        (js/console.log "[protect middleware] @user-attributes: " user-attributes))

      (when (and (true? is-bootstrapped)
                 (false? is-authenticated))
        (re-frame/dispatch
         [:router/nav-to
          (if (some? redirect-with)
            (redirect-with ctx)
            (str "/sign-in?redirect=" (-> js/window
                                          .-location
                                          .-pathname
                                          js/encodeURIComponent)))]))

      (if (or (not is-bootstrapped)
              (false? is-authenticated)
              (nil? (:sub user-attributes)))
        (assoc ctx :is-loading true)
        ctx))))

(defn redirect-authenticated
  "Redirects authenticated users to the authenticated home page"
  [{:keys [auth-redirect]}]
  (fn [{:keys [route-query] :as ctx}]
    (let [is-authenticated
          @(re-frame/subscribe [:authentication/is-authenticated?])

          is-bootstrapped
          @(re-frame/subscribe [:bootstrap/finished?])]
      (when (and (true? is-bootstrapped)
                 (true? is-authenticated))
        (re-frame/dispatch [:router/nav-to (auth-redirect route-query)]))

      (if (or (not (true? is-bootstrapped))
              (true? is-authenticated))
        (assoc ctx :is-loading true)
        ctx))))

(defn create-route-middleware-v2
  "Route middleware is an iterator that reduces over a sequence of
   middleware functions that enrich the route with additional data or
   modify it in some way.

   Use cases: Protecting routes
              Redirecting routes
              Bootstraping data"
  []
  (fn [container middleware]
    (let [is-bootstrapped (re-frame/subscribe [:bootstrap/finished?])
          route-params (re-frame/subscribe [:router/route-params])
          route-query (re-frame/subscribe [:router/route-query])
          route-key (re-frame/subscribe [:router/route])
          middleware-state (reagent/atom {:loading-resources {}
                                          :bootstrapped-resources {}})]
      (fn []
        (let [ctx
              (reduce
               (fn [middleware-ctx middleware-fn]
                 (middleware-fn middleware-ctx))
               {:middleware-state @middleware-state
                :route-params @route-params
                :route-query @route-query
                :route-key @route-key
                :is-bootstrapped @is-bootstrapped
                :container container}
               middleware)

              {:keys [loading-resources loading-state]}
              (:middleware-state ctx)

              {blocking-resources :resources
               loading-message :message}
              loading-state

              all-loaded
              (->> blocking-resources
                   (select-keys loading-resources)
                   (ku/all-resources-loaded?))

              waiting-on-resources
              (boolean (and (false? all-loaded)
                            (seq blocking-resources)))]

          (reset! middleware-state (:middleware-state ctx))

          (if (or (false? @is-bootstrapped)
                  (true? waiting-on-resources))
            [loading/render (cond-> {:is-bootstrapped @is-bootstrapped}
                              (some? loading-message)
                              (assoc :loading-message loading-message))]
            [container
             {:params @route-params
              :query @route-query
              :route-key @route-key
              :resource-status loading-resources}]))))))

(defn load-after
  ([resource-set]
   (load-after resource-set (i18n/t* ::data-message)))
  ([resource-set message]
   (fn [ctx]
     (if (nil? (get-in ctx [:middleware-state :loading-state]))
       (assoc-in ctx
                 [:middleware-state :loading-state]
                 {:message   message
                  :resources resource-set})
       ctx))))

(defn ensure-resource-v2
  "Ensures a resource has been bootstrapped. If it has not, an event will
  be dispatched and tracked.

  event-name        maps to the request name
  dispatch-fn       function that will be invoked with route params to trigger
                    the request
  wait-for-response Set of other request that need to be completed before
                    dispatching this request"
  ([event-name-sym-or-fn dispatch-fn ctx]
   (ensure-resource-v2 event-name-sym-or-fn dispatch-fn nil ctx))
  ([event-name-sym-or-fn
    dispatch-fn
    wait-for-resources
    {:keys [is-bootstrapped route-params route-query middleware-state] :as ctx}]
   (let [;; When event-name is supplied as a function it allows for building
         ;; up a dynamic event-name based on the context supplied. This is
         ;; useful when the container object may not updated the underlying
         ;; data may need to be refetched. (Ex: user dashboards with the side
         ;; pannel routing between learners.)
         event-name
         (if (fn? event-name-sym-or-fn)
           (event-name-sym-or-fn ctx)
           event-name-sym-or-fn)

         {:keys [loading-resources]}
         middleware-state

         is-requesting
         (re-frame/subscribe [:request/is-dispatching event-name])

         is-waiting
         (and (some? wait-for-resources)
              (->> wait-for-resources
                   (select-keys loading-resources)
                   (ku/all-resources-loaded?)
                   not))

         is-dispatching
         (= :dispatching (get-in middleware-state
                                 [:loading-resources event-name]))

         has-returned
         (and (true? is-dispatching)
              (false? @is-requesting))

         has-not-bootstrapped-request
         (nil? (get-in middleware-state [:bootstrapped-resources event-name]))

         should-make-request
         (and (true? is-bootstrapped)
              (true? has-not-bootstrapped-request)
              (false? is-waiting))

         next-middlware-state
         (cond-> middleware-state
           (true? should-make-request)
           (assoc-in [:bootstrapped-resources event-name] true)

           (true? should-make-request)
           (assoc-in [:loading-resources event-name] :dispatching)

           (true? has-returned)
           (assoc-in [:loading-resources event-name] :loaded))]

     (when (true? should-make-request)
       (dispatch-fn route-params route-query))

     ;; When the context is touched a rerender occurs
     (if (and (true? is-bootstrapped)
              (or (true? @is-requesting) ;; THIS NEEDS TO BE FIRST
                  (true? should-make-request)
                  (true? has-returned)))
       (assoc ctx :middleware-state next-middlware-state)
       ctx))))

(defn ensure-access-v2
  "Ensures the user has permission based on the permissions key in the user
   session. If a dispatch funcion is provided, it will be called when a user
   does not have access."
  ([permissions-key ctx]
   (ensure-access-v2 permissions-key nil ctx))
  ([permissions-key
    dispatch-fn
    {:keys [route-params] :as ctx}]
   (let [has-access?
         (re-frame/subscribe [:authentication/has-access? permissions-key])]
     (if (false? @has-access?)
       (when (fn? dispatch-fn)
         (dispatch-fn route-params))
       ctx))))

(defn redirect-to-sign-in
  "Redirects to a sign-in page"
  [_ route]
  (let [{:keys [pathname search]} (j/lookup js/location)
        encoded-redirect (js/encodeURIComponent (str pathname search))
        query (map->query {"redirect" encoded-redirect})]
    (str route "?" query)))

(defn redirect-from-sign-in
  [{:keys [route-query]} fallback]
  (let [redirect (get route-query "redirect")]
    (or redirect fallback)))

(defn redirect
  "Creates a clientside redirect passing all route parameters for a given route to a new route handler

  NOTE: This only works with the V2 middleware.
  NOTE: path-resolver is the function that resolves the path by wrapping bidi's `path-for` function.
  NOTE: All redirect paths should probably render the not-found container but are flexible enough to
        render anything for more advanced use-cases.

  Ex [... ;; other middleware
      (redirect :build-activity admin-path)]"
  [path-resolver]
  (fn [{:keys [route-params route-key] :as context}]
    (let [is-redirect (some-> route-key
                              name
                              (str/starts-with? "redirect-"))
          new-route-key (when is-redirect
                          (-> route-key
                              name
                              (str/split #"redirect-")
                              last
                              keyword))
          new-path (when (keyword? new-route-key)
                     (apply path-resolver (-> [new-route-key]
                                              (into route-params)
                                              flatten
                                              vec)))]
      (when (seq new-path)
        (re-frame/dispatch [:router/nav-to new-path]))
      context)))
